import { createFileRoute } from "@tanstack/react-router";
import { Download, FileCheck, CheckCircle2 } from "lucide-react";
import { PageHead, Panel } from "@/project/components/dashboard-shell";
import { sampleDonations, formatINR } from "@/project/data/sample";
import { downloadCsv, printReceipt } from "@/project/lib/receipt";

export const Route = createFileRoute("/donor/history")({ component: History });

function History() {
  return (
    <>
      <PageHead title="Donation history" subtitle="All your past donations and receipts." />
      <Panel
        title="All donations"
        action={
          <button
            onClick={() =>
              downloadCsv("my-donations.csv", [
                ["Donation ID", "Date", "Cause", "Organisation", "Amount", "Status"],
                ...sampleDonations.map((d) => [d.id, d.date, d.cause, d.institution, d.amount, d.status]),
              ])
            }
            className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-muted transition"
          >
            <Download className="h-3 w-3" /> Export CSV
          </button>
        }
      >
        {/* Mobile View: Cards List */}
        <div className="block md:hidden space-y-3">
          {sampleDonations.map((d) => {
            const isSuccess = d.status === "Success" || d.status === "success";
            return (
              <div
                key={d.id}
                className="rounded-2xl border border-border/80 bg-card p-4 shadow-xs space-y-3 transition"
              >
                <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-[var(--maroon-deep)] bg-[var(--maroon)]/10 px-2 py-0.5 rounded-md">
                      {d.id}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{d.date}</span>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                      isSuccess
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}
                  >
                    {isSuccess ? <CheckCircle2 className="h-3 w-3" /> : null}
                    {isSuccess ? "Completed" : d.status}
                  </span>
                </div>

                <div>
                  <p className="font-medium text-sm text-foreground leading-snug">{d.cause}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{d.institution}</p>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground block">Amount</span>
                    <span className="font-extrabold text-base text-[var(--maroon)]">{formatINR(d.amount)}</span>
                  </div>

                  {isSuccess ? (
                    <button
                      onClick={() =>
                        printReceipt({
                          id: d.id,
                          date: d.date,
                          donor: d.donor,
                          pan: "ABCDE1234F",
                          amount: d.amount,
                          purpose: d.cause,
                          beneficiary: d.institution,
                          frequency: "One-time",
                        })
                      }
                      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--maroon)]/30 bg-[var(--maroon)]/5 px-3 py-1.5 text-xs font-bold text-[var(--maroon)] hover:bg-[var(--maroon)] hover:text-white transition shadow-xs"
                    >
                      <FileCheck className="h-3.5 w-3.5" /> 80G Receipt
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground font-medium">Pending receipt</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop View: Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <tr className="text-left">
                <th className="py-3 px-2">ID</th>
                <th>Cause</th>
                <th>Institution</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th className="text-right pr-2">Receipt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {sampleDonations.map((d) => {
                const isSuccess = d.status === "Success" || d.status === "success";
                return (
                  <tr key={d.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3.5 px-2 font-mono text-xs font-bold text-muted-foreground">{d.id}</td>
                    <td className="max-w-xs font-medium text-foreground truncate">{d.cause}</td>
                    <td className="text-muted-foreground">{d.institution}</td>
                    <td className="font-extrabold text-[var(--maroon)]">{formatINR(d.amount)}</td>
                    <td className="text-xs text-muted-foreground">{d.date}</td>
                    <td>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                          isSuccess
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}
                      >
                        {isSuccess ? <CheckCircle2 className="h-3 w-3" /> : null}
                        {isSuccess ? "Completed" : d.status}
                      </span>
                    </td>
                    <td className="text-right pr-2">
                      {isSuccess ? (
                        <button
                          onClick={() =>
                            printReceipt({
                              id: d.id,
                              date: d.date,
                              donor: d.donor,
                              pan: "ABCDE1234F",
                              amount: d.amount,
                              purpose: d.cause,
                              beneficiary: d.institution,
                              frequency: "One-time",
                            })
                          }
                          className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1 text-xs font-medium hover:bg-[var(--cream-2)] hover:border-[var(--maroon)]/40 transition shadow-2xs"
                        >
                          <Download className="h-3 w-3 text-[var(--maroon)]" /> Receipt
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">On success</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}